import * as signalR from '@microsoft/signalr';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { downloadFileFromUrl } from 'src/utils/fileUtils';
import { FileExportProgress, FileExportResult } from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { ErrorTypes, useClientLogContext } from '../ClientLogContext';
import { POSSignalRHub } from '../SignalRConnection';

type IFileExportContext = {
  fileExportIsPending: boolean;
  setFileExportIsPending: (value: boolean, timeoutSecs?: number) => void;
  fileExportProgress: number;
  fileExportProgressFormatted: string;
  fileExportHub: POSSignalRHub | undefined;
};

const DEFAULT_MAX_TIMEOUT_SECS = 600;
const ON_FILE_RESULT_METHOD = 'on-file-export-result';
const ON_FILE_PROGRESS_METHOD = 'on-file-export-progress';

export const FileExportContext = React.createContext<IFileExportContext>({
  fileExportIsPending: false,
  setFileExportIsPending: () => {},
  fileExportProgress: 0,
  fileExportProgressFormatted: '0%',
  fileExportHub: undefined,
});

export const useFileExportContext = () => useContext(FileExportContext);

export const FileExportContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { activeAccountWebClientConfig } = useAppContext();

  const [fileExportIsPending, setFileExportIsPending] = React.useState(false);
  const [fileExportProgress, setFileExportProgress] = React.useState(0);
  const [isHubConnected, setIsHubConnected] = React.useState(false);
  const { showErrorDialog } = useClientLogContext();

  const { logEvent, trackError } = useClientLogContext();
  const hubConnection = useRef<POSSignalRHub>();

  useEffect(() => {
    if (!hubConnection.current) {
      hubConnection.current = new POSSignalRHub(
        '/hubs/fileexport',
        activeAccountWebClientConfig,
        logEvent
      );

      const fileHandler = async (message: FileExportResult) => {
        try {
          const fileName = message.fileName;
          const fileUri = message.fileUri;
          if (message.isSuccess) {
            downloadFileFromUrl(fileName!, fileUri!);
          } else {
            showErrorDialog(
              'FileExport',
              {
                message: message.errorMessage,
              },
              {
                trackErrorData: {
                  message,
                },
              }
            );
            console.error('FileExport - error: ' + message.errorMessage);
          }
          setFileExportIsPending(false);
        } catch (e) {
          console.error('FileExport - malformed SignalR Message: ' + message);
          trackError(
            'FileExportContextProvider.FailedToDownloadFile',
            e as ErrorTypes
          );
        } finally {
          setFileExportIsPending(false);
        }
      };

      const fileProgressHandler = (message: FileExportProgress) => {
        if (message?.percentage != null) {
          setFileExportProgress(message.percentage);
        }
      };

      hubConnection.current.on(ON_FILE_RESULT_METHOD, fileHandler);

      hubConnection.current.on(ON_FILE_PROGRESS_METHOD, fileProgressHandler);

      hubConnection.current
        .start()
        .then(() => {
          setIsHubConnected(true);
        })
        .finally(() => {
          console.debug(
            'FileExport connection ready with id: ' +
              hubConnection.current?.connectionId
          );
        })
        .catch(console.error);

      console.debug(
        'Creating new FileExport connection with id: ' +
          hubConnection.current.connectionId
      );
    }

    // Close the connection before unmount
    return () => {
      if (
        hubConnection.current &&
        hubConnection.current.state === signalR.HubConnectionState.Connected
      ) {
        console.debug(
          'Stopping FileExport connection with id: ' +
            hubConnection.current.connectionId
        );
        hubConnection.current.stop();
        hubConnection.current = undefined;
        setIsHubConnected(false);
      }
    };
  }, [activeAccountWebClientConfig, logEvent, showErrorDialog, trackError]);

  const setFileExportIsPendingWithTimeout = useCallback(
    (value: boolean, timeoutSecs?: number) => {
      setFileExportIsPending(value);
      setFileExportProgress(0);

      if (value) {
        setTimeout(
          () => {
            setFileExportIsPending(false);
          },
          (timeoutSecs ?? DEFAULT_MAX_TIMEOUT_SECS) * 1000
        );
      }
    },
    []
  );

  return (
    <FileExportContext.Provider
      value={{
        fileExportIsPending,
        setFileExportIsPending: setFileExportIsPendingWithTimeout,
        fileExportProgress,
        fileExportProgressFormatted: `${fileExportProgress}%`,
        fileExportHub: isHubConnected ? hubConnection.current : undefined,
      }}
    >
      {children}
    </FileExportContext.Provider>
  );
};
